import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { IdCard, LayoutDashboard, Briefcase, Network, FileText, ShieldCheck, ListChecks, Loader2, Plus, X, Trash2, Download, Check, Upload, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";
import { useAuth } from "../../context/AuthContext";

const ease = [0.16, 1, 0.3, 1];
const LEADERSHIP = ["admin", "ceo", "director"];

function Modal({ title, onClose, children, testid }) {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid={testid}>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }}
        className="card-surface w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-editorial text-2xl">{title}</h3>
          <button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </motion.div>
    </div>
  );
}

/* ============ PANEL ============ */
function DashboardTab() {
  const { t } = useI18n();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/hr/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const kpis = [
    { label: t("os.corehr.headcount"), value: d.headcount }, { label: t("os.corehr.records"), value: d.records_total },
    { label: t("os.corehr.positionsCount"), value: d.positions_total }, { label: t("os.corehr.onboarding"), value: d.active_onboarding },
    { label: t("os.corehr.offboarding"), value: d.active_offboarding }, { label: t("os.corehr.pendingAcks"), value: d.pending_policy_acks },
  ];
  const Bars = ({ title, rows, tx }) => (
    <div className="card-surface p-6">
      <div className="overline text-navy/40 mb-4">{title}</div>
      {(rows || []).length === 0 ? <p className="text-sm text-navy/35">—</p> : rows.map((r) => (
        <div key={r.label} className="flex items-center justify-between py-1.5 border-b border-navy/5 last:border-0">
          <span className="text-sm text-navy/70">{tx ? tx(r.label) : r.label}</span><span className="badge badge-violet">{r.count}</span>
        </div>
      ))}
    </div>
  );
  return (
    <div className="space-y-8" data-testid="corehr-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {kpis.map((k, i) => (
          <div key={k.label} className="card-surface p-5" data-testid={`corehr-dash-kpi-${i}`}>
            <div className="overline text-navy/40">{k.label}</div>
            <div className="mt-3 text-3xl font-medium tracking-tight">{k.value}</div>
          </div>
        ))}
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <Bars title={t("os.corehr.byDept")} rows={d.by_department} />
        <Bars title={t("os.corehr.byType")} rows={d.by_employment_type} tx={(x) => t(`os.corehr.empType.${x}`)} />
        <Bars title={t("os.corehr.byStatus")} rows={d.by_status} tx={(x) => t(`os.corehr.empStatus.${x}`)} />
      </div>
    </div>
  );
}

/* ============ EMPLEADOS ============ */
function EmployeeDialog({ emp, meta, positions, employees, onClose, onSaved }) {
  const { t } = useI18n();
  const [f, setF] = useState({
    employee_number: emp.employee_number || "", hire_date: emp.hire_date || "", employment_type: emp.employment_type || "",
    contract_type: emp.contract_type || "", position_id: emp.position_id || "", department: emp.department || "",
    work_location: emp.work_location || "", manager_id: emp.manager_id || "", status: emp.status || "activo",
    termination_date: emp.termination_date || "", salary: emp.salary ?? "", curp: emp.curp || "", rfc: emp.rfc || "",
    nss: emp.nss || "", emergency_contact: emp.emergency_contact || "", notes: emp.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    setSaving(true);
    try {
      const body = { ...f, salary: f.salary === "" ? null : Number(f.salary),
        position_id: f.position_id || null, manager_id: f.manager_id || null };
      await api.put(`/hr/employees/${emp.user_id}`, body);
      toast.success(t("os.corehr.recordSaved")); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <Modal title={`${t("os.corehr.editRecord")} · ${emp.name}`} onClose={onClose} testid="corehr-employee-dialog">
      <div className="grid sm:grid-cols-2 gap-3">
        <input className="field" placeholder={t("os.corehr.employeeNumber")} value={f.employee_number} onChange={set("employee_number")} data-testid="corehr-emp-number" />
        <input type="date" className="field" value={f.hire_date} onChange={set("hire_date")} data-testid="corehr-emp-hire" />
        <select className="field" value={f.employment_type} onChange={set("employment_type")} data-testid="corehr-emp-emptype">
          <option value="">{t("os.corehr.employmentType")}</option>
          {meta.employment_types.map((x) => <option key={x} value={x}>{t(`os.corehr.empType.${x}`)}</option>)}
        </select>
        <select className="field" value={f.contract_type} onChange={set("contract_type")}>
          <option value="">{t("os.corehr.contractType")}</option>
          {meta.contract_types.map((x) => <option key={x} value={x}>{t(`os.corehr.conType.${x}`)}</option>)}
        </select>
        <select className="field" value={f.position_id} onChange={set("position_id")} data-testid="corehr-emp-position">
          <option value="">{t("os.corehr.position")}</option>
          {positions.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
        <input className="field" placeholder={t("os.corehr.department")} value={f.department} onChange={set("department")} data-testid="corehr-emp-dept" />
        <input className="field" placeholder={t("os.corehr.workLocation")} value={f.work_location} onChange={set("work_location")} />
        <select className="field" value={f.manager_id} onChange={set("manager_id")}>
          <option value="">{t("os.corehr.manager")}</option>
          {employees.filter((e) => e.user_id !== emp.user_id).map((e) => <option key={e.user_id} value={e.user_id}>{e.name}</option>)}
        </select>
        <select className="field" value={f.status} onChange={set("status")} data-testid="corehr-emp-status">
          {meta.employee_statuses.map((x) => <option key={x} value={x}>{t(`os.corehr.empStatus.${x}`)}</option>)}
        </select>
        <input type="date" className="field" placeholder={t("os.corehr.terminationDate")} value={f.termination_date} onChange={set("termination_date")} />
        <input className="field" placeholder={t("os.corehr.salary")} type="number" value={f.salary} onChange={set("salary")} data-testid="corehr-emp-salary" />
        <input className="field" placeholder={t("os.corehr.curp")} value={f.curp} onChange={set("curp")} />
        <input className="field" placeholder={t("os.corehr.rfc")} value={f.rfc} onChange={set("rfc")} />
        <input className="field" placeholder={t("os.corehr.nss")} value={f.nss} onChange={set("nss")} />
        <input className="field sm:col-span-2" placeholder={t("os.corehr.emergencyContact")} value={f.emergency_contact} onChange={set("emergency_contact")} />
        <textarea className="field sm:col-span-2" rows={2} placeholder={t("os.corehr.notes")} value={f.notes} onChange={set("notes")} />
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="btn btn-ghost">{t("os.corehr.cancel")}</button>
        <button onClick={submit} disabled={saving} className="btn btn-primary" data-testid="corehr-emp-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("os.corehr.save")}
        </button>
      </div>
    </Modal>
  );
}

function EmployeesTab({ meta }) {
  const { t } = useI18n();
  const [employees, setEmployees] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, p] = await Promise.all([api.get("/hr/employees"), api.get("/hr/positions")]);
      setEmployees(e.data); setPositions(p.data);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const filtered = employees.filter((e) => !q || (e.name + e.email + e.department + e.position_title + e.employee_number).toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-5" data-testid="corehr-employees">
      <input className="field max-w-sm" placeholder={t("os.corehr.search")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="corehr-emp-search" />
      {loading ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
        : filtered.length === 0 ? <div className="card-surface py-16 text-center text-navy/40 text-sm">{t("os.corehr.noEmployees")}</div>
        : (
          <div className="overflow-x-auto card-surface">
            <table className="w-full text-sm" data-testid="corehr-emp-table">
              <thead><tr className="text-left text-navy/40 border-b border-navy/10">
                <th className="p-3 font-medium">{t("os.corehr.employee")}</th><th className="p-3 font-medium">{t("os.corehr.position")}</th>
                <th className="p-3 font-medium">{t("os.corehr.department")}</th><th className="p-3 font-medium">{t("os.corehr.employmentType")}</th>
                <th className="p-3 font-medium">{t("os.corehr.status")}</th><th className="p-3"></th>
              </tr></thead>
              <tbody>
                {filtered.map((e) => (
                  <tr key={e.user_id} className="border-b border-navy/5 hover:bg-navy/[0.02]" data-testid={`corehr-emp-row-${e.user_id}`}>
                    <td className="p-3"><div className="font-medium">{e.name}</div><div className="text-xs text-navy/40">{e.email}</div></td>
                    <td className="p-3 text-navy/70">{e.position_title || t("os.corehr.none")}</td>
                    <td className="p-3 text-navy/70">{e.department || t("os.corehr.none")}</td>
                    <td className="p-3 text-navy/70">{e.employment_type ? t(`os.corehr.empType.${e.employment_type}`) : t("os.corehr.none")}</td>
                    <td className="p-3"><span className={`badge ${e.status === "activo" ? "badge-emerald" : e.status === "baja" ? "badge-danger" : "badge-amber"}`}>{t(`os.corehr.empStatus.${e.status}`)}</span></td>
                    <td className="p-3 text-right"><button onClick={() => setEditing(e)} className="btn btn-ghost text-sm" data-testid={`corehr-emp-edit-${e.user_id}`}>{t("os.corehr.edit")}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      {editing && <EmployeeDialog emp={editing} meta={meta} positions={positions} employees={employees} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

/* ============ PUESTOS ============ */
function PositionsTab() {
  const { t } = useI18n();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", department: "", level: "", description: "", headcount_target: 1 });
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/hr/positions"); setPositions(r.data); } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const submit = async () => {
    if (!f.title.trim()) return toast.error(t("os.corehr.positionTitle"));
    setSaving(true);
    try {
      await api.post("/hr/positions", { ...f, headcount_target: Number(f.headcount_target) || 1 });
      toast.success(t("os.corehr.positionCreated")); setOpen(false);
      setF({ title: "", department: "", level: "", description: "", headcount_target: 1 }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  const del = async (id) => { try { await api.delete(`/hr/positions/${id}`); toast.success(t("os.corehr.positionDeleted")); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  return (
    <div className="space-y-5" data-testid="corehr-positions">
      <div className="flex justify-end"><button onClick={() => setOpen(true)} className="btn btn-primary" data-testid="corehr-new-position-btn"><Plus className="w-4 h-4" /> {t("os.corehr.newPosition")}</button></div>
      {loading ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
        : positions.length === 0 ? <div className="card-surface py-16 text-center text-navy/40 text-sm">{t("os.corehr.noPositions")}</div>
        : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="corehr-positions-grid">
            {positions.map((p) => (
              <div key={p.id} className="card-surface p-4" data-testid={`corehr-position-${p.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div><div className="font-medium">{p.title}</div><div className="text-xs text-navy/50">{p.department}{p.level ? ` · ${p.level}` : ""}</div></div>
                  <button onClick={() => del(p.id)} className="text-navy/30 hover:text-destructive" data-testid={`corehr-position-del-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
                {p.description && <p className="text-xs text-navy/50 mt-2">{p.description}</p>}
                <div className="mt-3 text-xs text-navy/50">{p.filled}/{p.headcount_target} {t("os.corehr.filled")}</div>
              </div>
            ))}
          </div>
        )}
      {open && (
        <Modal title={t("os.corehr.newPosition")} onClose={() => setOpen(false)} testid="corehr-position-dialog">
          <div className="space-y-3">
            <input className="field" placeholder={t("os.corehr.positionTitle")} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="corehr-position-title" />
            <input className="field" placeholder={t("os.corehr.department")} value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} />
            <input className="field" placeholder={t("os.corehr.level")} value={f.level} onChange={(e) => setF({ ...f, level: e.target.value })} />
            <textarea className="field" rows={2} placeholder={t("os.corehr.description")} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
            <input className="field" type="number" placeholder={t("os.corehr.headcountTarget")} value={f.headcount_target} onChange={(e) => setF({ ...f, headcount_target: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setOpen(false)} className="btn btn-ghost">{t("os.corehr.cancel")}</button>
            <button onClick={submit} disabled={saving} className="btn btn-primary" data-testid="corehr-position-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("os.corehr.create")}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ============ ORGANIGRAMA ============ */
function OrgTab() {
  const { t } = useI18n();
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/hr/orgchart").then((r) => setNodes(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  if (loading) return <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const byMgr = {};
  nodes.forEach((n) => { const k = n.manager_id || "__root__"; (byMgr[k] = byMgr[k] || []).push(n); });
  const ids = new Set(nodes.map((n) => n.id));
  const roots = nodes.filter((n) => !n.manager_id || !ids.has(n.manager_id));
  const Card = ({ n }) => (
    <div className="card-surface p-3 inline-block" data-testid={`corehr-org-${n.id}`}>
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-gradient-violet text-white flex items-center justify-center text-xs font-medium">{(n.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("")}</div>
        <div><div className="text-sm font-medium">{n.name}</div><div className="text-[11px] text-navy/45">{n.title || "—"}</div></div>
      </div>
    </div>
  );
  const renderTree = (n, depth) => (
    <div key={n.id} className="mt-2" style={{ marginLeft: depth * 24 }}>
      <div className="flex items-center gap-2">{depth > 0 && <span className="text-navy/20">└</span>}<Card n={n} /></div>
      {(byMgr[n.id] || []).map((c) => renderTree(c, depth + 1))}
    </div>
  );
  return (
    <div className="card-surface p-6 overflow-x-auto" data-testid="corehr-org">
      {roots.length === 0 ? <p className="text-sm text-navy/35">{t("os.corehr.none")}</p> : roots.map((r) => renderTree(r, 0))}
    </div>
  );
}

/* ============ DOCUMENTOS ============ */
function DocumentsTab({ meta, isLeader, myId }) {
  const { t } = useI18n();
  const [docs, setDocs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ user_id: myId, category: "otro", title: "", requires_ack: false, file: null });
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const reqs = [api.get("/hr/documents")];
      if (isLeader) reqs.push(api.get("/hr/employees"));
      const res = await Promise.all(reqs);
      setDocs(res[0].data);
      if (isLeader) setEmployees(res[1].data);
    } catch { /* noop */ } finally { setLoading(false); }
  }, [isLeader]);
  useEffect(() => { load(); }, [load]);
  const submit = async () => {
    if (!f.file) return toast.error(t("os.corehr.uploadDoc"));
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", f.file); fd.append("user_id", f.user_id || myId);
      fd.append("category", f.category); fd.append("title", f.title); fd.append("requires_ack", f.requires_ack);
      await api.post("/hr/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("os.corehr.docUploaded")); setOpen(false);
      setF({ user_id: myId, category: "otro", title: "", requires_ack: false, file: null }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  const download = async (d) => {
    try {
      const res = await api.get(`/hr/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = url; a.download = d.filename || "documento"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Error"); }
  };
  const ack = async (id) => { try { await api.post(`/hr/documents/${id}/ack`); toast.success(t("os.corehr.signed")); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  const del = async (id) => { try { await api.delete(`/hr/documents/${id}`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  return (
    <div className="space-y-5" data-testid="corehr-documents">
      <div className="flex justify-end"><button onClick={() => setOpen(true)} className="btn btn-primary" data-testid="corehr-upload-doc-btn"><Upload className="w-4 h-4" /> {t("os.corehr.uploadDoc")}</button></div>
      {loading ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
        : docs.length === 0 ? <div className="card-surface py-16 text-center text-navy/40 text-sm">{t("os.corehr.noDocs")}</div>
        : (
          <div className="space-y-2" data-testid="corehr-docs-list">
            {docs.map((d) => (
              <div key={d.id} className="card-surface p-4 flex flex-wrap items-center gap-3 justify-between" data-testid={`corehr-doc-${d.id}`}>
                <div className="min-w-0">
                  <div className="font-medium flex items-center gap-2"><FileText className="w-4 h-4 text-navy/40" /> {d.title}</div>
                  <div className="text-xs text-navy/50">{t(`os.corehr.docCat.${d.category}`)}{isLeader ? ` · ${d.user_name}` : ""} · {t("os.corehr.uploadedBy")} {d.uploaded_by_name}</div>
                </div>
                <div className="flex items-center gap-2">
                  {d.requires_ack && (d.my_ack ? <span className="badge badge-emerald">{t("os.corehr.signed")}</span>
                    : d.user_id === myId ? <button onClick={() => ack(d.id)} className="btn btn-outline text-sm" data-testid={`corehr-doc-ack-${d.id}`}><Check className="w-4 h-4" /> {t("os.corehr.sign")}</button>
                    : <span className="badge badge-amber">{t("os.corehr.pendingSign")}</span>)}
                  <button onClick={() => download(d)} className="btn btn-ghost text-sm" data-testid={`corehr-doc-dl-${d.id}`}><Download className="w-4 h-4" /></button>
                  {(isLeader || d.uploaded_by === myId) && <button onClick={() => del(d.id)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>}
                </div>
              </div>
            ))}
          </div>
        )}
      {open && (
        <Modal title={t("os.corehr.uploadDoc")} onClose={() => setOpen(false)} testid="corehr-doc-dialog">
          <div className="space-y-3">
            {isLeader && (
              <select className="field" value={f.user_id} onChange={(e) => setF({ ...f, user_id: e.target.value })} data-testid="corehr-doc-employee">
                <option value="">{t("os.corehr.selectEmployee")}</option>
                {employees.map((e) => <option key={e.user_id} value={e.user_id}>{e.name}</option>)}
              </select>
            )}
            <input className="field" placeholder={t("os.corehr.docTitle")} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="corehr-doc-title" />
            <select className="field" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
              {meta.doc_categories.map((c) => <option key={c} value={c}>{t(`os.corehr.docCat.${c}`)}</option>)}
            </select>
            <input type="file" className="field" onChange={(e) => setF({ ...f, file: e.target.files[0] })} data-testid="corehr-doc-file" />
            {isLeader && <label className="flex items-center gap-2 text-sm text-navy/60"><input type="checkbox" checked={f.requires_ack} onChange={(e) => setF({ ...f, requires_ack: e.target.checked })} data-testid="corehr-doc-ack-req" /> {t("os.corehr.requiresAck")}</label>}
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setOpen(false)} className="btn btn-ghost">{t("os.corehr.cancel")}</button>
            <button onClick={submit} disabled={saving} className="btn btn-primary" data-testid="corehr-doc-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("os.corehr.uploadDoc")}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ============ POLÍTICAS ============ */
function PoliciesTab({ meta, isLeader }) {
  const { t } = useI18n();
  const [pols, setPols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", body: "", category: "general", version: "1.0", requires_ack: true, published: true });
  const [saving, setSaving] = useState(false);
  const [acks, setAcks] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/hr/policies"); setPols(r.data); } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const submit = async () => {
    if (!f.title.trim()) return toast.error(t("os.corehr.policyTitle"));
    setSaving(true);
    try { await api.post("/hr/policies", f); toast.success(t("os.corehr.policyCreated")); setOpen(false);
      setF({ title: "", body: "", category: "general", version: "1.0", requires_ack: true, published: true }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  const ack = async (id) => { try { await api.post(`/hr/policies/${id}/ack`); toast.success(t("os.corehr.signed")); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  const del = async (id) => { try { await api.delete(`/hr/policies/${id}`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  const viewAcks = async (id) => { try { const r = await api.get(`/hr/policies/${id}/acks`); setAcks(r.data); } catch { /* noop */ } };
  return (
    <div className="space-y-5" data-testid="corehr-policies">
      {isLeader && <div className="flex justify-end"><button onClick={() => setOpen(true)} className="btn btn-primary" data-testid="corehr-new-policy-btn"><Plus className="w-4 h-4" /> {t("os.corehr.newPolicy")}</button></div>}
      {loading ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
        : pols.length === 0 ? <div className="card-surface py-16 text-center text-navy/40 text-sm">{t("os.corehr.noPolicies")}</div>
        : (
          <div className="grid gap-3 sm:grid-cols-2" data-testid="corehr-policies-grid">
            {pols.map((p) => (
              <div key={p.id} className="card-surface p-4" data-testid={`corehr-policy-${p.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0"><div className="font-medium">{p.title}</div><div className="text-xs text-navy/50">v{p.version} · {p.published ? t("os.corehr.published") : t("os.corehr.draft")}</div></div>
                  {isLeader && <button onClick={() => del(p.id)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>}
                </div>
                {p.body && <p className="text-sm text-navy/60 mt-2 line-clamp-3">{p.body}</p>}
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  {p.requires_ack && (p.my_ack ? <span className="badge badge-emerald">{t("os.corehr.signed")}</span>
                    : <button onClick={() => ack(p.id)} className="btn btn-outline text-sm" data-testid={`corehr-policy-ack-${p.id}`}><Check className="w-4 h-4" /> {t("os.corehr.sign")}</button>)}
                  {isLeader && <button onClick={() => viewAcks(p.id)} className="btn btn-ghost text-sm" data-testid={`corehr-policy-acks-${p.id}`}>{t("os.corehr.viewAcks")} ({p.ack_count ?? 0})</button>}
                </div>
              </div>
            ))}
          </div>
        )}
      {open && (
        <Modal title={t("os.corehr.newPolicy")} onClose={() => setOpen(false)} testid="corehr-policy-dialog">
          <div className="space-y-3">
            <input className="field" placeholder={t("os.corehr.policyTitle")} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="corehr-policy-title" />
            <select className="field" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
              {meta.policy_categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <textarea className="field" rows={5} placeholder={t("os.corehr.body")} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="corehr-policy-body" />
            <input className="field" placeholder={t("os.corehr.version")} value={f.version} onChange={(e) => setF({ ...f, version: e.target.value })} />
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm text-navy/60"><input type="checkbox" checked={f.requires_ack} onChange={(e) => setF({ ...f, requires_ack: e.target.checked })} /> {t("os.corehr.requiresAck")}</label>
              <label className="flex items-center gap-2 text-sm text-navy/60"><input type="checkbox" checked={f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} data-testid="corehr-policy-publish" /> {t("os.corehr.publish")}</label>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setOpen(false)} className="btn btn-ghost">{t("os.corehr.cancel")}</button>
            <button onClick={submit} disabled={saving} className="btn btn-primary" data-testid="corehr-policy-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("os.corehr.create")}</button>
          </div>
        </Modal>
      )}
      {acks && (
        <Modal title={t("os.corehr.viewAcks")} onClose={() => setAcks(null)} testid="corehr-acks-dialog">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><div className="overline text-navy/40 mb-2">{t("os.corehr.acked")} ({acks.acked_count})</div>{acks.acked.map((a) => <div key={a.user_id} className="py-1 text-navy/70">{a.name}</div>)}</div>
            <div><div className="overline text-navy/40 mb-2">{t("os.corehr.pending")} ({acks.pending.length})</div>{acks.pending.map((a) => <div key={a.user_id} className="py-1 text-navy/50">{a.name}</div>)}</div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ============ CICLO DE VIDA ============ */
function LifecycleTab({ isLeader }) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ user_id: "", type: "onboarding" });
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const reqs = [api.get("/hr/lifecycle", { params: { scope: isLeader ? "all" : "mine" } })];
      if (isLeader) reqs.push(api.get("/hr/employees"));
      const res = await Promise.all(reqs);
      setItems(res[0].data); if (isLeader) setEmployees(res[1].data);
    } catch { /* noop */ } finally { setLoading(false); }
  }, [isLeader]);
  useEffect(() => { load(); }, [load]);
  const submit = async () => {
    if (!f.user_id) return toast.error(t("os.corehr.selectEmployee"));
    setSaving(true);
    try { await api.post("/hr/lifecycle", f); toast.success(t("os.corehr.lifecycleStarted")); setOpen(false); setF({ user_id: "", type: "onboarding" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  const toggle = async (lid, item_id, done) => { try { await api.patch(`/hr/lifecycle/${lid}/item`, { item_id, done }); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  const del = async (lid) => { try { await api.delete(`/hr/lifecycle/${lid}`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  return (
    <div className="space-y-5" data-testid="corehr-lifecycle">
      {isLeader && <div className="flex justify-end"><button onClick={() => setOpen(true)} className="btn btn-primary" data-testid="corehr-start-lifecycle-btn"><Plus className="w-4 h-4" /> {t("os.corehr.startProcess")}</button></div>}
      {loading ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
        : items.length === 0 ? <div className="card-surface py-16 text-center text-navy/40 text-sm">{t("os.corehr.noLifecycle")}</div>
        : (
          <div className="grid gap-3 lg:grid-cols-2" data-testid="corehr-lifecycle-list">
            {items.map((it) => (
              <div key={it.id} className="card-surface p-4" data-testid={`corehr-lifecycle-${it.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div><div className="font-medium">{t(`os.corehr.procType.${it.type}`)}{isLeader ? ` · ${it.user_name}` : ""}</div>
                    <div className="text-xs text-navy/50">{it.progress}% {t("os.corehr.progress")} · {it.checklist.length} {t("os.corehr.steps")}</div></div>
                  <div className="flex items-center gap-2">
                    <span className={`badge ${it.status === "completed" ? "badge-emerald" : "badge-violet"}`}>{it.status === "completed" ? t("os.corehr.completed") : t("os.corehr.active")}</span>
                    {isLeader && <button onClick={() => del(it.id)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                </div>
                <div className="mt-3 space-y-1.5">
                  {it.checklist.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 text-sm text-navy/70 cursor-pointer" data-testid={`corehr-lc-item-${c.id}`}>
                      <input type="checkbox" checked={c.done} onChange={(e) => toggle(it.id, c.id, e.target.checked)} />
                      <span className={c.done ? "line-through text-navy/35" : ""}>{c.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      {open && (
        <Modal title={t("os.corehr.startProcess")} onClose={() => setOpen(false)} testid="corehr-lifecycle-dialog">
          <div className="space-y-3">
            <select className="field" value={f.user_id} onChange={(e) => setF({ ...f, user_id: e.target.value })} data-testid="corehr-lc-employee">
              <option value="">{t("os.corehr.selectEmployee")}</option>
              {employees.map((e) => <option key={e.user_id} value={e.user_id}>{e.name}</option>)}
            </select>
            <select className="field" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="corehr-lc-type">
              <option value="onboarding">{t("os.corehr.procType.onboarding")}</option>
              <option value="offboarding">{t("os.corehr.procType.offboarding")}</option>
            </select>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setOpen(false)} className="btn btn-ghost">{t("os.corehr.cancel")}</button>
            <button onClick={submit} disabled={saving} className="btn btn-primary" data-testid="corehr-lc-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("os.corehr.create")}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ============ MI EXPEDIENTE (empleado) ============ */
function MyRecordTab({ myId }) {
  const { t } = useI18n();
  const [emp, setEmp] = useState(null);
  useEffect(() => { api.get(`/hr/employees/${myId}`).then((r) => setEmp(r.data)).catch(() => {}); }, [myId]);
  if (!emp) return <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const rows = [
    [t("os.corehr.employeeNumber"), emp.employee_number], [t("os.corehr.hireDate"), emp.hire_date],
    [t("os.corehr.position"), emp.position_title], [t("os.corehr.department"), emp.department],
    [t("os.corehr.employmentType"), emp.employment_type ? t(`os.corehr.empType.${emp.employment_type}`) : ""],
    [t("os.corehr.contractType"), emp.contract_type ? t(`os.corehr.conType.${emp.contract_type}`) : ""],
    [t("os.corehr.workLocation"), emp.work_location], [t("os.corehr.status"), emp.status ? t(`os.corehr.empStatus.${emp.status}`) : ""],
    [t("os.corehr.curp"), emp.curp], [t("os.corehr.rfc"), emp.rfc], [t("os.corehr.nss"), emp.nss],
    [t("os.corehr.emergencyContact"), emp.emergency_contact],
  ];
  return (
    <div className="card-surface p-6" data-testid="corehr-myrecord">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-12 h-12 rounded-full bg-gradient-violet text-white flex items-center justify-center font-medium">{(emp.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("")}</div>
        <div><div className="font-editorial text-2xl">{emp.name}</div><div className="text-sm text-navy/50">{emp.position_title || emp.title || emp.role}</div></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1">
        {rows.map(([label, val]) => (
          <div key={label} className="flex justify-between py-2 border-b border-navy/5">
            <span className="text-sm text-navy/45">{label}</span><span className="text-sm text-navy/80">{val || t("os.corehr.none")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============ STUDIO ============ */
export default function CoreHR() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isLeader = LEADERSHIP.includes(user?.role);
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState(isLeader ? "dashboard" : "myrecord");

  useEffect(() => { api.get("/hr/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const leaderTabs = [
    { key: "dashboard", label: t("os.corehr.tabs.dashboard"), icon: LayoutDashboard },
    { key: "employees", label: t("os.corehr.tabs.employees"), icon: UsersIcon },
    { key: "positions", label: t("os.corehr.tabs.positions"), icon: Briefcase },
    { key: "org", label: t("os.corehr.tabs.org"), icon: Network },
    { key: "documents", label: t("os.corehr.tabs.documents"), icon: FileText },
    { key: "policies", label: t("os.corehr.tabs.policies"), icon: ShieldCheck },
    { key: "lifecycle", label: t("os.corehr.tabs.lifecycle"), icon: ListChecks },
  ];
  const employeeTabs = [
    { key: "myrecord", label: t("os.corehr.tabs.myrecord"), icon: IdCard },
    { key: "org", label: t("os.corehr.tabs.org"), icon: Network },
    { key: "documents", label: t("os.corehr.tabs.documents"), icon: FileText },
    { key: "policies", label: t("os.corehr.tabs.policies"), icon: ShieldCheck },
    { key: "lifecycle", label: t("os.corehr.tabs.lifecycle"), icon: ListChecks },
  ];
  const tabs = isLeader ? leaderTabs : employeeTabs;

  return (
    <OSLayout title={t("os.corehr.title")}>
      <div className="space-y-8" data-testid="corehr-page">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
          <div className="overline text-navy/40 mb-2 flex items-center gap-2"><IdCard className="w-3.5 h-3.5" /> {t("os.corehr.overline")}</div>
          <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">{t("os.corehr.title")}</h2>
          <p className="mt-2 text-navy/50 max-w-2xl">{t("os.corehr.subtitle")}</p>
        </motion.div>

        <div className="flex flex-wrap gap-2 border-b border-navy/10 pb-1">
          {tabs.map((tb) => (
            <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`corehr-tab-${tb.key}`}
              className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm transition-colors ${tab === tb.key ? "bg-gradient-violet text-white" : "text-navy/55 hover:text-navy hover:bg-navy/5"}`}>
              <tb.icon className="w-3.5 h-3.5" /> {tb.label}
            </button>
          ))}
        </div>

        {!meta ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
          <>
            {tab === "dashboard" && isLeader && <DashboardTab />}
            {tab === "employees" && isLeader && <EmployeesTab meta={meta} />}
            {tab === "positions" && isLeader && <PositionsTab />}
            {tab === "org" && <OrgTab />}
            {tab === "documents" && <DocumentsTab meta={meta} isLeader={isLeader} myId={user?.id} />}
            {tab === "policies" && <PoliciesTab meta={meta} isLeader={isLeader} />}
            {tab === "lifecycle" && <LifecycleTab isLeader={isLeader} />}
            {tab === "myrecord" && !isLeader && <MyRecordTab myId={user?.id} />}
          </>
        )}
      </div>
    </OSLayout>
  );
}
